import { FormItemTypes, IDictionary, MLHelper, createGuid } from '@bimser/core';
import get from 'lodash/get';
import DataGrid2 from '..';
import {
	IBaseExecuteActionArgs,
	ParameterItemType
} from '../../../../../entities/common/executeActionArgs';
import { IUserEvent } from '../../../../../entities/services';
import {
	IActionButtonClickEventArgs,
	IBeforeToolbarButtonClickEventArgs,
	IToolbarButtonClickEventArgs
} from '../../../entities/EventArgs';
import { IControlProperties } from '../../../entities/IControlProperties';
import {
	IActionButtonClickRowColumn,
	IDataGridColumnCollection,
	IExecuteActionButton
} from '../../DataGrid/entities';
import { IDataGridColumn, IDataGridExecuteActionButton, IDataGridRowCollection } from '../entities';

const isColumn = (v: string) => v.match(/\[([^\[\]]+)\]/g);
const convertValue = (v: string | number, row: any) =>
	typeof v === 'string' && isColumn(v) ? row[v.slice(1, -1)] : v;

function getCommandColumnButtonArgs(props: IExecuteActionButton<any>, row: any, castedArgs: any) {
	switch (props.typeName) {
		case 'CreateFormArgs': {
			castedArgs = { ...props.args };

			castedArgs.formName = convertValue(castedArgs.formName, row);
			castedArgs.formName = convertValue(castedArgs.formName, row);
			castedArgs.projectName = convertValue(castedArgs.projectName, row);

			break;
		}
		case 'OpenFormArgs': {
			castedArgs = { ...props.args };

			castedArgs.documentId = convertValue(castedArgs.documentId, row);

			break;
		}
		case 'OpenProcessArgs': {
			castedArgs = { ...props.args };

			castedArgs.processId = convertValue(castedArgs.processId, row);
			castedArgs.processRequestId = convertValue(castedArgs.processRequestId, row);

			break;
		}
		case 'StartAProcessArgs': {
			castedArgs = { ...props.args };

			castedArgs.flowName = convertValue(castedArgs.flowName, row);
			castedArgs.projectName = convertValue(castedArgs.projectName, row);

			break;
		}
	}
	return castedArgs;
}

export async function onColumnActionButtonClick(
	dataGrid: DataGrid2,
	column: IDataGridColumn<IControlProperties>,
	dataGridRow: IDictionary<any>
) {
	const row: IActionButtonClickRowColumn[] = Object.keys(dataGridRow || {}).map(columnName => {
		const rowValue = dataGridRow[columnName];
		const col = dataGrid.columns.find(c => c.name === columnName);

		let text = null;
		let value = null;

		if (col && rowValue !== null && rowValue !== undefined && rowValue !== '') {
			switch (col.editType) {
				case 'comboBox':
					text = rowValue.text;
					value = rowValue.value;

					break;
				case 'multiLanguageTextBox':
					text = MLHelper.getMLText(rowValue);
					value = MLHelper.getMLText(rowValue);

					break;
				case 'dateRangePicker':
					text = `${rowValue[0] || ''} / ${rowValue[1] || ''}`;
					value = rowValue;

					break;
				default:
					text = rowValue.toString?.();
					value = rowValue;

					break;
			}

			return { name: columnName, text, value };
		}

		return { name: columnName, text: null, value: null };
	});

	const argument: IActionButtonClickEventArgs = {
		name: column.name,
		row
	};

	const result = await dataGrid.__form.__runServerEvent({
		argument,
		controlId: dataGrid.name,
		name: 'ActionButtonClick',
		sender: 'web'
	});

	dataGrid.__form.__executedServerEvent = result.executedServerEvent;

	if (result.loadActionData.isDMFileSecretModified) {
		dataGrid.__form.__loadDocumentViewer();
	}

	if (result.loadActionData.isRDFileSecretModified) {
		dataGrid.__form.isLoadingRDObjects = false;
		dataGrid.__form.__getRDObjects();
	}

	const resultColumn: IDataGridColumnCollection = get(
		result.loadActionData,
		['items', 'entities', 'items', dataGrid.name, 'properties', 'columns'],
		null
	)?.find(i => i.name === column.name);

	const castedArgs = getCommandColumnButtonArgs(
		column.actionButtonProps,
		dataGridRow,
		column.actionButtonProps.args
	);

	column.actionButtonProps.args = convertActionButtonParameterValues(
		dataGrid,
		column.actionButtonProps.args,
		dataGridRow
	);

	onActionButtonClick(dataGrid, {
		...column.actionButtonProps,
		args: {
			...castedArgs,
			secretKey: get(resultColumn, ['actionButtonProps', 'args', 'secretKey'], null)
		}
	});

	dataGrid.emit<IActionButtonClickEventArgs>('event:OnActionButtonClick', argument);
}

export const onBeforeToolbarItemClick = async (
	dataGrid: DataGrid2,
	item: IDataGridExecuteActionButton<any>
) => {
	const beforeArgs: IBeforeToolbarButtonClickEventArgs = {
		cancel: false,
		name: item.name
	};

	await dataGrid.emit('event:OnBeforeToolbarButtonClick', beforeArgs);

	if (!beforeArgs.cancel) {
		await onToolbarItemClick(dataGrid, item);
	}
};

const onToolbarItemClick = async (dataGrid: DataGrid2, item: IDataGridExecuteActionButton<any>) => {
	item.args = convertActionButtonParameterValues(dataGrid, item.args);

	await dataGrid.emit<IToolbarButtonClickEventArgs>('event:OnToolbarButtonClick', {
		name: item.name
	});

	await dataGrid.emit<IUserEvent<IToolbarButtonClickEventArgs>>(
		'event:server:OnToolbarButtonClick',
		{
			argument: { name: item.name },
			controlId: dataGrid.name,
			name: 'OnToolbarButtonClick',
			sender: 'web',
			guid: createGuid()
		}
	);

	onActionButtonClick(dataGrid, item);
};

const onActionButtonClick = (dataGrid: DataGrid2, item: IDataGridExecuteActionButton<any>) => {
	switch (item.typeName) {
		case 'CreateFormArgs': {
			dataGrid.__form.createForm(item.args, dataGrid.name);
			break;
		}
		case 'OpenFormArgs': {
			if (dataGrid.__isReadOnly()) {
				dataGrid.__form.openForm({ ...item.args, readOnly: true }, dataGrid.name);
				break;
			}

			dataGrid.__form.openForm(item.args, dataGrid.name);
			break;
		}
		case 'OpenProcessArgs': {
			dataGrid.__form.openProcess(item.args, dataGrid.name);
			break;
		}
		case 'OpenSelectionFormArgs': {
			if (dataGrid.columns.length) {
				item.args.targetDataGridColumns = dataGrid.columns;
			}

			item.args.gridId = `Runtime_${dataGrid.__form.projectName}_${dataGrid.__form.name}_${dataGrid.name}_OSFGrid`;
			dataGrid.__form.openSelectionForm(item.args, dataGrid.name, item.title);
			break;
		}
		case 'StartAProcessArgs': {
			dataGrid.__form.startAProcess(item.args, dataGrid.name);
			break;
		}
		case 'ExecuteCustomActionArgs':
			break;
		default:
			console.log('Execute action type not found!');
	}
};

const convertActionButtonParameterValues = (
	dataGrid: DataGrid2,
	args: IBaseExecuteActionArgs<ParameterItemType>,
	row: IDictionary<any> = {}
) => {
	args.parameters.forEach(param => {
		switch (param.type) {
			case 'column': {
				param._value = get(row, param.value);
				break;
			}
			case 'field': {
				param._value = get(dataGrid.__form, param.value);

				if (param._value == undefined) {
					const [controlName, field, columnName] = param.value.split('.');

					if (
						columnName &&
						(field === 'rows' || field === 'columns') &&
						dataGrid.__form[controlName].controlType ==
							FormItemTypes.EControlTypes.DataGrid
					) {
						const rows: IDataGridRowCollection = get(
							dataGrid.__form,
							`${controlName}.rows`
						);

						param._value = rows.map(
							row => row.cells.find(cell => cell.name == columnName).value
						);
					}
				}

				break;
			}
			case 'selectedRow': {
				const selectedRowKey = dataGrid.selectedRowKeys.at(0);
				if (selectedRowKey && param.value) {
					const keyExpr = dataGrid.getUniqueDataField();
					const selectedRow = dataGrid.rows.find(row => row[keyExpr] === selectedRowKey);

					if (selectedRow) {
						param._value = selectedRow.data[param.value];
					}
				}
				break;
			}
		}
	});

	return args;
};
